/**
 * admin-settlements.ts - 정산(Settlement) 관련 Firestore 조회 서비스
 *
 * 경로: users/{ownerUid}/centers/{centerId}/settlements/{yearMonth}
 */

import { doc, getDoc, type Timestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import type {
  SettlementStatus,
  SettlementLineItem,
  InstructorSettlement,
} from "@vlab-corporation/vlab-pilates-service-interface/settlement";

export type { SettlementLineItem, InstructorSettlement };

/**
 * 정산 문서 타입 (Firestore 조회용)
 *
 * service-interface의 Settlement 타입을 기반으로 하되,
 * Firestore SDK가 Date 대신 Timestamp를 반환하므로 calculatedAt 필드만 오버라이드한다.
 */
export interface AdminSettlementDocument {
  yearMonth: string;
  status: SettlementStatus;
  instructorSettlements: InstructorSettlement[];
  calculatedAt: Timestamp | null;
}

/**
 * 특정 월의 정산 문서를 Firestore에서 조회한다.
 *
 * @param ownerUid - 센터 소유자 UID
 * @param centerId - 센터 ID
 * @param yearMonth - 조회 대상 연월 ("YYYY-MM" 형식)
 * @returns 정산 문서 또는 null (문서 없는 경우)
 */
export async function getAdminSettlement(
  ownerUid: string,
  centerId: number,
  yearMonth: string,
): Promise<AdminSettlementDocument | null> {
  const ref = doc(
    getFirebaseFirestore(),
    "users",
    ownerUid,
    "centers",
    String(centerId),
    "settlements",
    yearMonth,
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as AdminSettlementDocument;
}
